import type { UserRole } from "@the-drop/types";
import { getDispensaryApiBaseUrl } from "./api-base";
import { fetchWithClerkJwt } from "./fetch-with-clerk";

/** Subset of **`GET /api/v1/dispensaries/:id`** / admin list for account UI. */
export type DispensaryPublicSummary = {
  id: string;
  legalName: string;
  dbaName: string | null;
  publicSlug: string | null;
  medLicenseNumber: string;
  medLicenseType: string;
  medLicenseExpiresAt: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  metrcLicenseNumber: string;
  dutchieLocationId: string | null;
  posAdapter: string | null;
  /** ADR-132 — who reports this location's deliveries to Metrc. Older APIs omit it. */
  metrcDeliveryReporter?: "drop" | "pos";
  isActive: boolean;
  onboardingCompletedAt: string | null;
  /** ISO time of the last banner upload, null when the shop has no custom banner. Older APIs omit it. */
  storefrontBannerUpdatedAt?: string | null;
};

export type MeDispensaryMembership = {
  dispensaryId: string;
  /** Display name — `dbaName ?? legalName`. */
  name: string;
  /** Short disambiguator within a brand (e.g. "Aurora"); null if unset. */
  locationLabel: string | null;
  roles: UserRole[];
};

export type MeDispensaryMembershipsResponse = {
  /** Set when the user is org-scoped (multi-location manager); null otherwise. */
  orgId: string | null;
  memberships: MeDispensaryMembership[];
};

export async function fetchMeDispensaryMemberships(
  getToken: () => Promise<string | null>,
): Promise<MeDispensaryMembershipsResponse> {
  const res = await fetchWithClerkJwt(getToken, "/api/v1/me/dispensary-memberships");
  if (!res.ok) {
    return { orgId: null, memberships: [] };
  }
  return (await res.json()) as MeDispensaryMembershipsResponse;
}

export async function fetchDispensaryPublicSummary(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
): Promise<DispensaryPublicSummary | null> {
  const res = await fetchWithClerkJwt(getToken, `/api/v1/dispensaries/${encodeURIComponent(dispensaryId)}`);
  if (!res.ok) return null;
  return (await res.json()) as DispensaryPublicSummary;
}

/** Mirrors the API's accepted storefront-banner types (migration 050). */
export const STOREFRONT_BANNER_CONTENT_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;

export type StorefrontBannerUploadResult =
  | { ok: true; updatedAt: string }
  | { ok: false; error: "unsupported_type" | "too_large" | "upload_failed" };

/** Decoded cap enforced server-side; checked here first for a friendlier error. */
export const STOREFRONT_BANNER_MAX_BYTES = 4 * 1024 * 1024;

/**
 * **`PUT /api/v1/dispensaries/:id/storefront-banner`** — upload the customer-menu
 * hero image (jpeg/png/webp ≤ 4 MB) as base64 JSON.
 */
export async function uploadDispensaryStorefrontBanner(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
  file: File,
): Promise<StorefrontBannerUploadResult> {
  if (!(STOREFRONT_BANNER_CONTENT_TYPES as readonly string[]).includes(file.type)) {
    return { ok: false, error: "unsupported_type" };
  }
  if (file.size > STOREFRONT_BANNER_MAX_BYTES) {
    return { ok: false, error: "too_large" };
  }
  const bytes = new Uint8Array(await file.arrayBuffer());
  // btoa over the whole buffer at once can blow the call stack — chunk it.
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  const res = await fetchWithClerkJwt(
    getToken,
    `/api/v1/dispensaries/${encodeURIComponent(dispensaryId)}/storefront-banner`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ imageBase64: btoa(binary), contentType: file.type }),
    },
  );
  if (res.status === 413) return { ok: false, error: "too_large" };
  if (!res.ok) return { ok: false, error: "upload_failed" };
  const body = (await res.json()) as { updatedAt: string };
  return { ok: true, updatedAt: body.updatedAt };
}

/** **`DELETE /api/v1/dispensaries/:id/storefront-banner`** — revert to the placeholder. */
export async function deleteDispensaryStorefrontBanner(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
): Promise<boolean> {
  const res = await fetchWithClerkJwt(
    getToken,
    `/api/v1/dispensaries/${encodeURIComponent(dispensaryId)}/storefront-banner`,
    { method: "DELETE" },
  );
  return res.ok;
}

/**
 * Public banner-image URL for a shop with a `publicSlug` — used by the
 * settings preview (same bytes the customer menu shows). `version` cache-busts
 * after an upload.
 */
export function dispensaryStorefrontBannerPreviewUrl(publicSlug: string, version: string): string {
  const base = getDispensaryApiBaseUrl();
  const path = `/api/v1/dispensaries/${encodeURIComponent(publicSlug)}/storefront-banner?v=${encodeURIComponent(version)}`;
  return base ? `${base}${path}` : path;
}

/** **`GET /api/v1/admin/dispensaries`** — platform admin only. */
export async function fetchAdminDispensariesList(
  getToken: () => Promise<string | null>,
): Promise<DispensaryPublicSummary[] | null> {
  const res = await fetchWithClerkJwt(getToken, "/api/v1/admin/dispensaries");
  if (res.status === 403 || res.status === 401) return null;
  if (!res.ok) return null;
  const data = (await res.json()) as { dispensaries?: DispensaryPublicSummary[] };
  return data.dispensaries ?? [];
}

/** Body for **`POST /api/v1/dispensaries`** (platform admin). METRC fields feed outbound Metrc + webhook inventory. */
export type CreateDispensaryRequest = {
  legalName: string;
  medLicenseNumber: string;
  medLicenseType: "retail" | "medical" | "dual";
  medLicenseExpiresAt: string;
  addressLine1: string;
  city: string;
  zip: string;
  phone: string;
  email: string;
  metrcLicenseNumber: string;
  metrcApiKey: string;
  state?: string;
  dbaName?: string;
  publicSlug?: string;
  clerkOrgId?: string;
  addressLine2?: string;
  hasDeliveryPermit?: boolean;
  deliveryPermitNumber?: string | null;
  deliveryPermitExpiresAt?: string;
  dutchieApiKey?: string;
  dutchieLocationId?: string | null;
  posAdapter?: string;
};

export type CreatedDispensary = DispensaryPublicSummary & {
  secrets: {
    metrcApiKeyConfigured: boolean;
    dutchieApiKeyConfigured: boolean;
    straddlePaykeyConfigured: boolean;
    plaidItemConfigured: boolean;
    plaidAccessTokenConfigured: boolean;
  };
};

export async function createDispensary(
  getToken: () => Promise<string | null>,
  body: CreateDispensaryRequest,
): Promise<
  | { ok: true; dispensary: CreatedDispensary }
  | { ok: false; status: number; error: string; details?: unknown; field?: string }
> {
  const res = await fetchWithClerkJwt(getToken, "/api/v1/dispensaries", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const raw = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (res.status === 201) {
    return { ok: true, dispensary: raw as CreatedDispensary };
  }
  const err = typeof raw.error === "string" ? raw.error : "create_failed";
  const field = typeof raw.field === "string" ? raw.field : undefined;
  const details = raw.details;
  return { ok: false, status: res.status, error: err, details, field };
}
